/**
 * Cipher Crack — provably-fair Mastermind engine.
 *
 * Server commits a 4-digit code (each digit independently uniform on [0,9])
 * before the round starts. Player has 5 guesses; each guess returns peg
 * feedback:
 *   - "gold"   = digit correct AND in correct slot
 *   - "silver" = digit correct, slot wrong
 *   - "miss"   = digit not in code
 *
 * Standard Mastermind double-counting: each code digit can only "feed" one
 * peg total — golds first, then silvers — so duplicates don't double-count.
 *
 * Crack on attempt N → payout = stake × PAYOUT_BY_ATTEMPT[N-1].
 * 5 attempts spent without crack → bust, lose stake.
 *
 * Determinism via xmur3 + mulberry32 (seed primitives shared with Pressure
 * Plate). Server seed is committed pre-round, revealed on resolve so the
 * player can re-derive the code and verify.
 */

import {
  commitSeed,
  hashServerSeed,
  mulberry32,
  xmur3,
} from "../pressure-plate/engine.js";

export { commitSeed, hashServerSeed };

// ---- Constants ----------------------------------------------------------

export const CODE_LEN = 4;
export const MAX_GUESSES = 4;
/** Round time limit in seconds. Round auto-busts when the timer expires
 *  even if the player has unspent guesses. */
export const ROUND_TIME_S = 30;

/** Crack payout indexed by (attempt-1) where attempt is 1-based.
 *  Tuned for slot-mapped pegs + 4-guess limit (harder than the 5-guess
 *  variant, so multipliers are bumped to compensate). */
export const PAYOUT_BY_ATTEMPT: ReadonlyArray<number> = [20, 5, 2.5, 1.3];

/** Cashout multiplier indexed by (number of wrong guesses so far). Index 0
 *  is no-cashout (just launched, must guess first). All values are STRICTLY
 *  BELOW 1.0× — cashout is always a partial loss, never free profit.
 *  Otherwise the spam-and-cash-out exploit (3 random guesses → guaranteed
 *  cashout > 1.0×) breaks the house edge. */
export const CASH_OUT_MULTIPLIERS: ReadonlyArray<number> = [0, 0.35, 0.65, 0.95];

// ---- Public types -------------------------------------------------------

export type Outcome = "cracked" | "sealed";
export type Peg = "gold" | "silver" | "miss";

export type CodeTuple = [number, number, number, number];
export type PegTuple = [Peg, Peg, Peg, Peg];

export interface CipherCrackRoundSpec {
  serverSeed: string;
  clientSeed: string;
  nonce: number;
  bet: number;
}

export interface Guess {
  /** Each digit 0..9. */
  digits: CodeTuple;
  pegs: PegTuple;
}

export interface CipherCrackRoundState {
  spec: CipherCrackRoundSpec;
  bet: number;
  /** The committed code. Hidden during play, revealed on resolve. */
  code: CodeTuple;
  guesses: Guess[];
  outcome: Outcome | null; // null while round is live
  /** 1..5 if cracked, null otherwise. */
  crackedAt: number | null;
}

// ---- Code derivation ----------------------------------------------------

/** Deterministic from (serverSeed, clientSeed, nonce). 4 independent digits. */
export function deriveCode(
  serverSeed: string,
  clientSeed: string,
  nonce: number,
): CodeTuple {
  const r = mulberry32(xmur3(`${serverSeed}:${clientSeed}:${nonce}:cipher`)());
  return [
    Math.floor(r() * 10),
    Math.floor(r() * 10),
    Math.floor(r() * 10),
    Math.floor(r() * 10),
  ];
}

// ---- Peg computation (Mastermind feedback) ------------------------------

/**
 * Two-pass peg compute:
 *   Pass 1: mark golds (right digit, right slot). Consume both code and
 *           guess slots so they can't feed silvers in pass 2.
 *   Pass 2: for each remaining guess slot, look for the digit anywhere in
 *           remaining code slots; if found, mark silver and consume.
 *
 * This handles duplicates correctly: e.g. guess `[1,1,2,3]` against code
 * `[2,1,3,4]` returns `[miss, gold, miss, miss]`, not `[silver, gold, ...]`.
 */
export function computePegs(
  guess: ReadonlyArray<number>,
  code: ReadonlyArray<number>,
): PegTuple {
  const pegs: Peg[] = ["miss", "miss", "miss", "miss"];
  const codeRem: Array<number | null> = [code[0]!, code[1]!, code[2]!, code[3]!];
  const guessRem: Array<number | null> = [guess[0]!, guess[1]!, guess[2]!, guess[3]!];
  // Pass 1 — golds
  for (let i = 0; i < CODE_LEN; i += 1) {
    if (guessRem[i] === codeRem[i]) {
      pegs[i] = "gold";
      codeRem[i] = null;
      guessRem[i] = null;
    }
  }
  // Pass 2 — silvers
  for (let i = 0; i < CODE_LEN; i += 1) {
    const g = guessRem[i];
    if (g === null || g === undefined) continue;
    const j = codeRem.indexOf(g);
    if (j !== -1) {
      pegs[i] = "silver";
      codeRem[j] = null;
    }
  }
  return [pegs[0]!, pegs[1]!, pegs[2]!, pegs[3]!];
}

/** True iff every peg is gold (the code is fully matched). */
export function isCrack(pegs: PegTuple): boolean {
  return pegs[0] === "gold" && pegs[1] === "gold" && pegs[2] === "gold" && pegs[3] === "gold";
}

// ---- Payout -------------------------------------------------------------

export function multiplierForAttempt(attempt: number): number {
  return PAYOUT_BY_ATTEMPT[attempt - 1] ?? 0;
}

/** Cashout multiplier for the current state. Pass the count of wrong
 *  guesses so far (0..4). Returns 0 if cashout isn't allowed yet (no
 *  guesses) or for invalid input. */
export function cashoutMultiplier(wrongGuesses: number): number {
  return CASH_OUT_MULTIPLIERS[wrongGuesses] ?? 0;
}

// ---- Verifier -----------------------------------------------------------

/**
 * Re-derive the code from the spec and confirm it matches the claimed code
 * the round resolved with. Used by the verify modal after the seed reveals.
 */
export function verify(
  spec: CipherCrackRoundSpec,
  claimedCode: ReadonlyArray<number>,
): boolean {
  const r = deriveCode(spec.serverSeed, spec.clientSeed, spec.nonce);
  if (claimedCode.length !== CODE_LEN) return false;
  return r.every((d, i) => d === claimedCode[i]);
}
